import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, Button, Typography, Alert, CircularProgress, Snackbar
} from '@mui/material';
import api from '../../services/api';

const emptyPolicy = { minimumSupportedBuild: '', latestAvailableBuild: '', appStoreURL: '', message: '' };

export default function AppVersionPolicyContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(emptyPolicy);
  const [saved, setSaved] = useState(emptyPolicy);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchPolicy(); }, []);

  const toForm = (p) => ({
    minimumSupportedBuild: p?.minimumSupportedBuild == null ? '' : String(p.minimumSupportedBuild),
    latestAvailableBuild: p?.latestAvailableBuild == null ? '' : String(p.latestAvailableBuild),
    appStoreURL: p?.appStoreURL || '',
    message: p?.message || ''
  });

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await api.get('/config/app-version-policy');
      if (response.data.success) {
        const form = toForm(response.data.appVersionPolicy);
        setPolicy(form);
        setSaved(form);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load the app version policy', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const raising = policy.minimumSupportedBuild.trim() !== ''
      && policy.minimumSupportedBuild.trim() !== saved.minimumSupportedBuild;
    if (raising && !window.confirm(
      `Every iPhone and iPad running a build below ${policy.minimumSupportedBuild.trim()} will be blocked until it updates, and its sync will pause. Continue?`
    )) {
      return;
    }
    try {
      setSaving(true);
      const response = await api.put('/config/app-version-policy', {
        minimumSupportedBuild: policy.minimumSupportedBuild.trim() === '' ? null : policy.minimumSupportedBuild.trim(),
        latestAvailableBuild: policy.latestAvailableBuild.trim() === '' ? null : policy.latestAvailableBuild.trim(),
        appStoreURL: policy.appStoreURL.trim(),
        message: policy.message.trim()
      });
      if (response.data.success) {
        const form = toForm(response.data.appVersionPolicy);
        setPolicy(form);
        setSaved(form);
        setSnackbar({ open: true, message: 'App version policy saved', severity: 'success' });
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save the app version policy';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>iOS Builds</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        A device running an older build shows a full-screen update prompt, pauses its own sync, and is refused
        by the relay until it updates. Nothing on the device is deleted. Leave the minimum empty to allow every build.
        Builds released before this setting existed cannot read it. The latest build is separate: a device that is
        still allowed, but older than that build, shows a dismissible notice only after the app has finished
        loading. Update opens the App Store page. Not now hides that build. Sync keeps running.
      </Alert>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Minimum build number"
            value={policy.minimumSupportedBuild}
            onChange={(e) => setPolicy(prev => ({ ...prev, minimumSupportedBuild: e.target.value }))}
            helperText="The CFBundleVersion, e.g. 30. Empty means no minimum."
            inputProps={{ min: 1, step: 1 }}
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="App Store link"
            value={policy.appStoreURL}
            onChange={(e) => setPolicy(prev => ({ ...prev, appStoreURL: e.target.value }))}
            helperText="https://apps.apple.com/… — required when a minimum or a latest build is set. Update opens this page."
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Latest build on the App Store"
            value={policy.latestAvailableBuild}
            onChange={(e) => setPolicy(prev => ({ ...prev, latestAvailableBuild: e.target.value }))}
            helperText="The CFBundleVersion on the App Store. Empty means no notice. Older apps ignore it."
            inputProps={{ min: 1, step: 1 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Message shown on the update screen (optional)"
            value={policy.message}
            onChange={(e) => setPolicy(prev => ({ ...prev, message: e.target.value }))}
            inputProps={{ maxLength: 500 }}
            helperText="Shown only on the full-screen required update. Leave empty for the app's standard wording."
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={24} /> : 'Save App Version Policy'}
        </Button>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
